import type { SimDecisionResults, SimShareRequest, SimShareResponse } from '@cruz/shared';
import { load, save } from '../lib/storage.ts';
import { useEffect, useState } from 'react';
import { ChallengePage } from './Challenge.tsx';
import { ReportButton } from './Report.tsx';
import { Sheet } from './Sheet.tsx';
import { navigate } from '../challenge/router.ts';
import { simApi, SimApiError, simErrorMessage } from './api.ts';
import { Meter, Notice, PageTitle, primaryButton, secondaryButton, Working } from './ui.tsx';

/** Share cards: the sheet that makes one and shares it, and its public page. */

const shareText = (s: SimShareResponse) =>
  s.card.kind === 'tramo'
    ? `Te reto a ordenar mi tramo de nazarenos. ${s.card.phrase} Es medio minuto y no hace falta registrarse.`
    : s.card.kind === 'decision'
      ? `¿Tú qué habrías hecho? En mi hermandad me ha pasado esto: «${s.card.title}». Vota y descubre qué elegí yo.`
      : `${s.card.phrase} — ${s.card.nickname}, ${s.card.brotherhood}. ¿Te atreves con tu carrera cofrade?`;

const smallButton =
  'inline-flex min-h-10 items-center gap-1.5 rounded-full border border-gold/70 bg-paper px-4 py-1.5 ' +
  'text-sm font-semibold text-purple transition-colors hover:bg-cream-200 dark:border-gold/50 ' +
  'dark:bg-night-700 dark:text-gold-300 dark:hover:bg-night-800';

export function ShareButton({
  request,
  label = 'Compartir',
}: {
  request: SimShareRequest;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={smallButton} onClick={() => setOpen(true)}>
        <svg
          width={16}
          height={16}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M16 6l-4-4-4 4M12 2v14" />
        </svg>
        {label}
      </button>
      {open && <ShareSheet request={request} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareSheet({ request, onClose }: { request: SimShareRequest; onClose: () => void }) {
  const [share, setShare] = useState<SimShareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** A guest: sharing is the moment to save the career */
  const [needsAccount, setNeedsAccount] = useState(false);
  const [copied, setCopied] = useState(false);
  const { kind, ref } = request;

  useEffect(() => {
    let alive = true;
    simApi
      .share({ kind, ref })
      .then((s) => alive && setShare(s))
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof SimApiError && err.code === 'register_required') setNeedsAccount(true);
        else setError(simErrorMessage(err));
      });
    return () => {
      alive = false;
    };
  }, [kind, ref]);

  const canShare = typeof navigator.share === 'function';
  const nativeShare = async () => {
    if (!share) return;
    const data: ShareData = { title: share.card.title, text: shareText(share), url: share.url };
    try {
      // With the image when the phone can (Android, iOS 15+)
      const blob = await (await fetch(share.image_story)).blob();
      const file = new File([blob], `logro-${share.card.id}.png`, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) data.files = [file];
    } catch {
      // Without the image: the link still has its preview
    }
    try {
      await navigator.share(data);
    } catch {
      // Cancelled
    }
  };
  const copy = async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Sheet title="Compártelo" onClose={onClose}>
      {!share && !error && !needsAccount && <Working label="Preparando tu tarjeta…" />}
      {needsAccount && (
        <div className="space-y-3">
          <p className="text-lg">
            Para compartir tus logros, guarda antes tu carrera: así tus amigos verán tu apodo y no
            la pierdes.
          </p>
          <button
            type="button"
            className={primaryButton}
            onClick={() => {
              onClose();
              navigate('/simulador/registro');
            }}
          >
            Guardar mi carrera
          </button>
        </div>
      )}
      {error && <Notice tone="error">{error}</Notice>}
      {share && (
        <div className="space-y-3">
          <img
            src={share.image_story}
            alt={`Tarjeta: ${share.card.kicker}. ${share.card.title}. ${share.card.phrase}`}
            width={1080}
            height={1920}
            className="mx-auto h-auto max-h-[46dvh] w-auto rounded-xl border border-gold/60 shadow-lg"
          />
          {canShare && (
            <button type="button" className={primaryButton} onClick={() => void nativeShare()}>
              Compartir
            </button>
          )}
          <a
            className={canShare ? secondaryButton : primaryButton}
            href={`https://wa.me/?text=${encodeURIComponent(`${shareText(share)} ${share.url}`)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Enviar por WhatsApp
          </a>
          <a
            className={secondaryButton}
            href={share.image_story}
            download={`logro-${share.card.id}.png`}
          >
            Descargar imagen
          </a>
          <button type="button" className={secondaryButton} onClick={() => void copy()}>
            {copied ? '¡Enlace copiado!' : 'Copiar enlace'}
          </button>
        </div>
      )}
    </Sheet>
  );
}

/** A random id for this browser, so a friend votes once on each decision. */
export function voterId(): string {
  const saved = load<string | null>(VOTER_KEY, null);
  if (saved) return saved;
  const id = crypto.randomUUID().replaceAll('-', '');
  save(VOTER_KEY, id);
  return id;
}
export const VOTER_KEY = 'cruz.sim.voter';
/** The card a visitor came from: their career starts with it (see SimApp). */
export const REF_KEY = 'cruz.sim.ref';

/** /simulador/logro/:id — what friends see when they open a shared card. */
export function SharePage({ id, onPlay }: { id: string; onPlay: () => Promise<void> }) {
  const [share, setShare] = useState<SimShareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    // Whoever starts from here, came from this card
    save(REF_KEY, id);
    simApi
      .shared(id, load<string | null>(VOTER_KEY, null))
      .then(setShare)
      .catch((err: unknown) => setError(simErrorMessage(err)));
  }, [id]);
  if (error) {
    return (
      <div className="space-y-4">
        <Notice tone="error">Esta tarjeta ya no existe.</Notice>
        <button type="button" className={primaryButton} onClick={() => navigate('/simulador')}>
          Conocer el juego
        </button>
      </div>
    );
  }
  if (!share) return <Working label="Cargando…" />;
  const c = share.card;
  if (c.kind === 'decision' && c.decision) return <DecisionPage share={share} onPlay={onPlay} />;
  if (c.kind === 'tramo' && c.tramo) {
    return (
      <ChallengePage
        share={share}
        voter={voterId}
        cta={<PlayCta onPlay={onPlay} label="Vivir mi propia carrera" />}
        report={
          <ReportButton
            what="este reto"
            onSend={(reason, comment) => simApi.reportShare(id, reason, comment)}
          />
        }
      />
    );
  }
  return (
    <div className="space-y-5">
      <PageTitle kicker={c.kicker}>{c.title}</PageTitle>
      <img
        src={share.image_story}
        alt={`Tarjeta de ${c.nickname}: ${c.phrase}`}
        width={1080}
        height={1920}
        className="mx-auto h-auto max-h-[60dvh] w-auto rounded-2xl border border-gold/60 shadow-lg"
      />
      <p className="text-lg">
        <strong>{c.nickname}</strong> juega a <em>De cirio a hermano mayor</em> con su hermandad,{' '}
        {c.brotherhood}. Funda la tuya y empieza de cirio en el primer tramo.
      </p>
      <PlayCta onPlay={onPlay} />
      <ReportButton
        what="esta tarjeta"
        onSend={(reason, comment) => simApi.reportShare(id, reason, comment)}
      />
    </div>
  );
}

export function PlayCta({
  onPlay,
  label = 'Empezar mi carrera',
}: {
  onPlay: () => Promise<void>;
  label?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="space-y-3">
      <button
        type="button"
        className={primaryButton}
        disabled={busy}
        onClick={() => {
          setBusy(true);
          onPlay()
            .catch((err: unknown) => setError(simErrorMessage(err)))
            .finally(() => setBusy(false));
        }}
      >
        {busy ? 'Abriendo la nómina…' : label}
      </button>
      <p className="text-center text-sm text-ink-soft dark:text-cream/70">
        Sin registrarte. Al empezar aceptas las{' '}
        <a href="/simulador/condiciones" className="underline">
          condiciones
        </a>{' '}
        y la{' '}
        <a href="/simulador/privacidad" className="underline">
          privacidad
        </a>
        .
      </p>
      {error && <Notice tone="error">{error}</Notice>}
      <button type="button" className={secondaryButton} onClick={() => navigate('/simulador')}>
        ¿Qué es esto?
      </button>
    </div>
  );
}

const pctOf = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

/** A shared decision: vote first, then see what they chose, what happened and what others voted. */
function DecisionPage({ share, onPlay }: { share: SimShareResponse; onPlay: () => Promise<void> }) {
  const c = share.card;
  const d = c.decision!;
  const [results, setResults] = useState<SimDecisionResults | null>(share.results ?? null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const own = Boolean(results) && results!.mine === null;
  const vote = async (choice: number) => {
    setBusy(choice);
    setError(null);
    try {
      setResults(await simApi.vote(c.id, choice, voterId()));
      scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(simErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="space-y-5">
      <PageTitle kicker="¿Tú qué habrías hecho?">{c.title}</PageTitle>
      <p className="text-ink-soft dark:text-cream/80">
        A <strong className="text-ink dark:text-cream">{c.nickname}</strong>,{' '}
        {c.rank_label.toLowerCase()} de {c.brotherhood}, le ha pasado esto en su hermandad:
      </p>
      <p className="rounded-2xl border border-purple/15 bg-paper px-5 py-4 text-[1.08rem] leading-relaxed whitespace-pre-line dark:border-cream/15 dark:bg-night-800">
        {d.text}
      </p>
      {!results ? (
        <>
          <h2 id="vote" className="font-serif text-2xl font-semibold">
            ¿Qué harías tú?
          </h2>
          <ul aria-labelledby="vote" className="space-y-3">
            {d.options.map((o, i) => (
              <li key={i}>
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void vote(i)}
                  className="flex min-h-14 w-full items-start gap-3 rounded-xl border border-purple/20 bg-paper px-4 py-3 text-left text-[1.05rem] leading-snug transition hover:border-gold disabled:opacity-60 dark:border-cream/20 dark:bg-night-800"
                >
                  <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-purple font-serif text-sm font-semibold text-cream dark:bg-gold-300 dark:text-night">
                    {i + 1}
                  </span>
                  <span>{o}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="text-sm text-ink-soft dark:text-cream/70">
            Vota y descubre qué eligió {c.nickname} y qué le pasó.
          </p>
          {error && <Notice tone="error">{error}</Notice>}
        </>
      ) : (
        <section aria-labelledby="results" className="space-y-4">
          <h2 id="results" className="font-serif text-2xl font-semibold">
            {own ? 'Lo que han votado tus amigos' : `${c.nickname} eligió…`}
          </h2>
          <ul className="space-y-3">
            {d.options.map((o, i) => {
              const p = pctOf(results.votes[i] ?? 0, results.total);
              return (
                <li
                  key={i}
                  className={`rounded-xl border px-4 py-3 ${i === results.chosen ? 'border-gold bg-cream-200 dark:bg-gold/10' : 'border-purple/15 dark:border-cream/15'}`}
                >
                  <p className="text-[1.02rem] leading-snug">{o}</p>
                  <p className="mt-1 flex flex-wrap gap-x-3 text-sm font-semibold">
                    {i === results.chosen && (
                      <span className="text-gold-700 dark:text-gold-300">
                        {own ? 'Lo que elegiste' : `Lo que eligió ${c.nickname}`}
                      </span>
                    )}
                    {i === results.mine && <span>Tu voto</span>}
                  </p>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex-1">
                      <Meter value={p} label={`${o}: ${p} % de los votos`} />
                    </div>
                    <span className="w-12 text-right text-sm tabular">{p} %</span>
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="text-sm text-ink-soft dark:text-cream/70">
            {results.total === 1 ? 'Un voto' : `${results.total} votos`} de{' '}
            {own ? 'tus amigos' : `los amigos de ${c.nickname}`}.
          </p>
          <div className="rounded-2xl border-l-4 border-gold bg-paper px-5 py-4 dark:bg-night-800">
            <p className="text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase dark:text-gold-300">
              Lo que pasó
            </p>
            <p className="mt-1 text-lg">{results.outcome}</p>
          </div>
        </section>
      )}
      {!own && (
        <div className="space-y-3 border-t border-gold/40 pt-5">
          <p className="text-lg">
            En <em>De cirio a hermano mayor</em> fundas tu hermandad y decides cada día. Empiezas de
            cirio, en el primer tramo.
          </p>
          <PlayCta onPlay={onPlay} label="Vivir mi propia carrera" />
        </div>
      )}
      <ReportButton
        what="esta tarjeta"
        onSend={(reason, comment) => simApi.reportShare(c.id, reason, comment)}
      />
    </div>
  );
}
